import { useEffect, useRef } from 'react';

/** Matches the `eposHop` keyframes in styles/index.css. */
export const HOP_MS = 360;

/**
 * Makes a number "hop" when it changes: attach the returned ref to the element
 * that shows `value`. The hop (a small swell in place that settles back) is
 * the move for sums and counts that should draw the eye - cart count, order
 * total. Stepped quantities roll instead, see Components/RollingNumber.
 *
 * Toggles the `is-hopping` class straight on the node, so nothing renders for
 * the animation itself. The first render never hops; only real changes do.
 */
export const useHopOnChange = <T extends HTMLElement>(value: unknown) => {
  const nodeRef = useRef<T>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const isFirstRef = useRef(true);

  useEffect(() => {
    if (isFirstRef.current) {
      isFirstRef.current = false;
      return undefined;
    }
    const node = nodeRef.current;
    if (!node) {
      return undefined;
    }
    node.classList.remove('is-hopping');
    // Forces the animation to restart even when the class was just removed.
    void node.offsetWidth;
    node.classList.add('is-hopping');
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => node.classList.remove('is-hopping'), HOP_MS);
    return () => window.clearTimeout(timerRef.current);
  }, [value]);

  return nodeRef;
};

export default useHopOnChange;
